import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AlertMsg from '../../component/AlertMsg/AlertMsg';
import Loading from "../../component/Loading/Loading";
import useAxiosInstance from '../../services/useAxiosInstance';
import { Lock, Key, Save, X } from 'lucide-react';

const ChangePassword = () => {
  const axiosInstance = useAxiosInstance();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [userData, setUserData] = useState(null);
  const [oldPassword, setOldPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const userId = useSelector((state) => state.auth.userId);
  const history = useHistory();

  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axiosInstance.get(`/api/user/getById/${userId}`);
        setUserData(response.data);
        if (response.data.password) {
          setOldPassword(response.data.password);
        }
      } catch (err) {
        setErrors((prevErrors) => ({ ...prevErrors, fetchUserData: 'Failed to fetch user data. Please try again.' }));
      }
    };

    fetchUserData();
  }, [userId]);

  const validateForm = (currentPassword, password, confirmPassword, userData) => {
    const newErrors = {};
    if (!currentPassword) newErrors.currentPassword = 'Current password is required.';
    if (!password) newErrors.password = 'New password is required.';
    if (!confirmPassword) newErrors.confirmPassword = 'Confirming new password is required.';
    if (password !== confirmPassword) newErrors.confirmPassword = 'New passwords do not match.';
    if (userData && userData.password !== currentPassword) newErrors.currentPassword = 'Current password is incorrect.';
    return newErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors({});
    setSuccess('');
    const validationErrors = validateForm(currentPassword, password, confirmPassword, userData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);

    try {
      await axiosInstance.patch(`/api/user/updateUserPassword/${userId}?password=${password}`);
      setAlert({ visible: true, message: 'Password changed successfully!', type: 'success' });
      setTimeout(() => history.goBack(), 2000);
      setLoading(false);
    } catch (err) {
      setAlert({ visible: true, message: 'Failed to change password: Please try again.', type: 'error' });
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}

          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Reset Password</h1>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="p-6">
              <div className="max-w-2xl mx-auto">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Key size={18} className="text-gray-400" />
                      </div>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className={`w-full rounded-md border ${errors.currentPassword ? "border-red-500" : "border-gray-300"} pl-10 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>
                    {errors.currentPassword && (
                      <p className="text-red-500 text-xs mt-1">{errors.currentPassword}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock size={18} className="text-gray-400" />
                      </div>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`w-full rounded-md border ${errors.password ? "border-red-500" : "border-gray-300"} pl-10 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock size={18} className="text-gray-400" />
                      </div>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`w-full rounded-md border ${errors.confirmPassword ? "border-red-500" : "border-gray-300"} pl-10 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      />
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                    )}
                  </div>

                  <div className="text-sm text-red-500">
                    * Required fields
                  </div>

                  <div className="flex justify-end space-x-3 pt-2">
                    <button
                      type="button"
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      onClick={() => history.goBack()}
                    >
                      <X size={18} />
                      <span>Cancel</span>
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
                    >
                      <Save size={18} />
                      <span>Save Changes</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChangePassword;